import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDate } from "@/lib/format";

function SidePanel({ info }) {
  return (
    <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1729] p-12 lg:flex">
      <div className="absolute inset-0 opacity-[0.12]"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FFB000 0, transparent 45%), radial-gradient(circle at 80% 70%, #4A78A8 0, transparent 40%)" }} />
      <div className="relative z-10"><Logo variant="dark" /></div>
      <div className="relative z-10 max-w-md">
        <div className="label-eyebrow mb-4 text-[#FFC24D]">Team Workspace</div>
        <h1 className="font-display text-4xl font-extrabold leading-tight text-white">
          {info?.company_name ? `Join ${info.company_name}.` : "Join your team."}
        </h1>
        <p className="mt-5 text-base leading-relaxed text-white/60">
          See the work you've been given, how it links to the business plan, the KPIs you report and the resources the company shares with you.
        </p>
      </div>
      <div className="relative z-10 text-xs text-white/30">© {new Date().getFullYear()} Amden Group</div>
    </div>
  );
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/workspace/invitations/validate/${encodeURIComponent(token)}`)
      .then((r) => { setInfo(r.data); setStatus("valid"); })
      .catch((e) => { setReason(apiError(e.response?.data?.detail)); setStatus("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setSaving(true);
    try {
      const { data } = await api.post("/workspace/invitations/accept", { token, password });
      setResult(data);
      setStatus("done");
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F6F9]">
      <SidePanel info={info} />
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md" data-testid="accept-invite-page">
          <div className="mb-8 lg:hidden"><Logo variant="light" /></div>
          {status === "checking" && (
            <div className="flex items-center gap-3 text-[#1E2D44]/60"><Icons.Loader2 className="animate-spin" size={18} /> Checking your invitation…</div>
          )}
          {status === "invalid" && (
            <div data-testid="accept-invite-invalid">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#C95555]/10 text-[#C95555]"><Icons.Unlink size={22} /></div>
              <h2 className="font-display text-3xl font-bold text-[#0B1729]">This invitation can't be used</h2>
              <p className="mt-2 text-sm text-[#1E2D44]/60" data-testid="accept-invite-reason">
                {reason || "This invitation link is invalid, has expired or has been withdrawn."}
              </p>
              <p className="mt-2 text-sm text-[#1E2D44]/60">Invitations work once and last 7 days. Ask your manager or administrator to send you a new one.</p>
              <Button onClick={() => navigate("/login")} className="mt-6 h-11 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="accept-back-to-login">Go to sign in</Button>
            </div>
          )}
          {status === "valid" && (
            <>
              <div className="label-eyebrow mb-2">You're invited</div>
              <h2 className="font-display text-3xl font-bold text-[#0B1729]" data-testid="accept-invite-name">Hi {info.name.split(" ")[0]}</h2>
              <p className="mt-2 text-sm text-[#1E2D44]/60">
                <span className="font-medium text-[#0B1729]">{info.company_name}</span> has set up your Team Workspace
                {info.job_title ? <> as <span className="font-medium text-[#0B1729]">{info.job_title}</span></> : null}.
                Choose a password for <span className="font-medium text-[#0B1729]">{info.email}</span>.
              </p>
              <p className="mt-1 text-xs text-[#1E2D44]/45">This link expires on {fmtDate(info.expires_at)}.</p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="ai-pw">Password</Label>
                  <Input id="ai-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 h-11 bg-white" data-testid="accept-password" />
                </div>
                <div>
                  <Label htmlFor="ai-pw2">Confirm password</Label>
                  <Input id="ai-pw2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1.5 h-11 bg-white" data-testid="accept-confirm-password" />
                </div>
                {error && (
                  <div data-testid="accept-invite-error" className="flex items-center gap-2 rounded-lg bg-[#C95555]/10 px-3 py-2.5 text-sm text-[#C95555]"><Icons.CircleAlert size={16} /> {error}</div>
                )}
                <Button type="submit" disabled={saving} className="h-11 w-full bg-[#FFB000] text-base font-semibold text-[#0B1729] hover:bg-[#E69E00]" data-testid="accept-invite-submit">
                  {saving ? <Icons.Loader2 className="animate-spin" size={18} /> : "Accept invitation"}
                </Button>
              </form>
            </>
          )}
          {status === "done" && (
            <div data-testid="accept-invite-done">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#3D8B65]/10 text-[#3D8B65]"><Icons.CheckCircle2 size={22} /></div>
              <h2 className="font-display text-3xl font-bold text-[#0B1729]">You're in</h2>
              <p className="mt-2 text-sm text-[#1E2D44]/60">
                {result?.bound_existing_account
                  ? `Your existing Compass login (${result.email}) is now connected to ${info.company_name}. Sign in with your current password.`
                  : `Your account is ready. Sign in with ${result?.email} and the password you just chose.`}
              </p>
              <Button onClick={() => navigate("/login", { replace: true })} className="mt-6 h-11 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="accept-go-to-login">Sign in</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
